import { Droplets, Gauge, Navigation, Umbrella, Wind, Cloud } from "lucide-react";
import { kmh, localTime, wIcon, windDir } from "@/lib/weather";
import { useT } from "@/lib/i18n";

const Big = ({ icon: Icon, label, value, unit, testid, accent = "text-white", extra }) => (
  <div data-testid={testid} className="bg-[#0B0D13] border border-[#282D3D] rounded-sm px-4 py-3">
    <div className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-slate-400"><Icon className="w-4 h-4" />{label}</div>
    <div className={`flex items-baseline gap-1.5 font-heading font-extrabold text-4xl leading-tight tabular-nums mt-1 ${accent}`}>
      {value}{unit && <span className="text-lg text-slate-400 font-bold">{unit}</span>}{extra}
    </div>
  </div>
);

const Small = ({ label, value, testid }) => (
  <div data-testid={testid} className="flex flex-col"><span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span><span className="font-heading font-bold text-xl tabular-nums">{value}</span></div>
);

export const CurrentNow = ({ data }) => {
  const { t, lang } = useT();
  const c = data.current;
  const w = c.weather?.[0] || {};
  const Icon = wIcon(w.icon);
  const off = data.timezone_offset;
  const rain1h = c.rain?.["1h"] ?? 0;
  return (
    <section data-testid="weather-current" className="bg-[#12141C] border border-[#282D3D] rounded-sm p-4 sm:p-5 h-full flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <Icon className="w-20 h-20 text-[#FFB800] shrink-0" strokeWidth={1.4} />
        <div className="min-w-0">
          <div data-testid="weather-current-temp" className="font-heading font-extrabold text-8xl leading-none tabular-nums">{Math.round(c.temp)}°</div>
          <div data-testid="weather-current-desc" className="font-heading uppercase text-2xl text-slate-200 truncate">{w.description}</div>
          <div className="font-mono text-xs text-slate-400">{t("wx_now")} {localTime(c.dt, off)} · {t("wx_feels")} {Math.round(c.feels_like)}°</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Big testid="weather-wind" icon={Wind} label={t("wx_wind")} value={kmh(c.wind_speed)} unit="km/h"
          extra={<span className="flex items-center gap-1 text-xl text-[#FFB800] ml-1"><Navigation className="w-5 h-5" style={{ transform: `rotate(${(c.wind_deg || 0) + 135}deg)` }} />{windDir(c.wind_deg, lang)}</span>} />
        <Big testid="weather-gust" icon={Wind} label={t("wx_gust")} value={c.wind_gust != null ? kmh(c.wind_gust) : "—"} unit={c.wind_gust != null ? "km/h" : ""} />
        <Big testid="weather-humidity" icon={Droplets} label={t("wx_humidity")} value={c.humidity} unit="%" accent="text-[#60A5FA]" />
        <Big testid="weather-rain-1h" icon={Umbrella} label={t("wx_rain_1h")} value={rain1h.toFixed(1)} unit="mm" accent={rain1h > 0 ? "text-[#60A5FA]" : "text-white"} />
        <Big testid="weather-pressure" icon={Gauge} label={t("wx_pressure")} value={c.pressure} unit="hPa" />
        <Big testid="weather-clouds" icon={Cloud} label={t("wx_clouds")} value={c.clouds} unit="%" />
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 border-t border-[#282D3D] pt-3">
        <Small testid="weather-dew" label={t("wx_dew")} value={`${Math.round(c.dew_point)}°`} />
        <Small testid="weather-visibility" label={t("wx_visibility")} value={c.visibility != null ? `${(c.visibility / 1000).toFixed(1)} km` : "—"} />
        <Small testid="weather-uv" label="UV" value={Math.round(c.uvi ?? 0)} />
        <Small testid="weather-sunrise" label={t("wx_sunrise")} value={localTime(c.sunrise, off)} />
        <Small testid="weather-sunset" label={t("wx_sunset")} value={localTime(c.sunset, off)} />
      </div>
    </section>
  );
};
